import { z } from 'zod/v4';

import type { VehicleProfile } from '@klareospace/pharos-lib-types';
import { VEHICLE_PROFILES } from '@klareospace/pharos-lib-types';

// ─── Messages ─────────────────────────────────────────────────────────────────

export interface VehicleTemplateSchemaMessages {
  codeFormat: string;
  codeRequired: string;
  labelRequired: string;
  maxVolumeRequired: string;
  maxWeightRequired: string;
  nonNegative: string;
  positiveInteger: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const VEHICLE_PROFILE_VALUES = Object.values(VEHICLE_PROFILES) as [VehicleProfile, ...VehicleProfile[]];

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createVehicleTemplateSchema(
  isEditMode: boolean,
  messages: VehicleTemplateSchemaMessages,
): z.ZodObject<{
  code: z.ZodString;
  maxOrderCapacity: z.ZodString;
  maxVolumeUnit: z.ZodEnum<{ L: 'L'; m3: 'm3' }>;
  maxVolumeValue: z.ZodString;
  maxWeightUnit: z.ZodEnum<{ kg: 'kg'; lb: 'lb' }>;
  maxWeightValue: z.ZodString;
  name: z.ZodString;
  profile: z.ZodEnum<Record<VehicleProfile, VehicleProfile>>;
}> {
  const codeSchema = isEditMode
    ? z.string().trim().min(1, messages.codeRequired)
    : z
        .string()
        .trim()
        .min(1, messages.codeRequired)
        .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, messages.codeFormat);

  return z.object({
    code: codeSchema,
    maxOrderCapacity: z
      .string()
      .refine((v) => v === '' || (Number.isInteger(Number(v)) && parseInt(v, 10) >= 1), messages.positiveInteger),
    maxVolumeUnit: z.enum(['m3', 'L']),
    maxVolumeValue: z
      .string()
      .min(1, messages.maxVolumeRequired)
      .refine((v) => !isNaN(parseFloat(v)) && parseFloat(v) >= 0, messages.nonNegative),
    maxWeightUnit: z.enum(['kg', 'lb']),
    maxWeightValue: z
      .string()
      .min(1, messages.maxWeightRequired)
      .refine((v) => !isNaN(parseFloat(v)) && parseFloat(v) >= 0, messages.nonNegative),
    name: z.string().trim().min(1, messages.labelRequired),
    profile: z.enum(VEHICLE_PROFILE_VALUES),
  });
}

export type VehicleTemplateFormInput = z.infer<ReturnType<typeof createVehicleTemplateSchema>>;
